import { Component, Host, h, Prop, Element, EventEmitter, Event, Listen, State, Watch } from '@stencil/core';

@Component({
    tag: 'sew-ws-button',
    styleUrl: 'sew-ws-button.scss',
    shadow: true,
})
export class SewWsButton {
    @Element() el: HTMLSewWsButtonElement;

    /**
     * Number that get's displayed
     */
    @Prop() public counter: string;

    /**
     * Ellipsis
     */
    @Prop({ reflect: true }) ellipsis: boolean;

    @Prop({ reflect: true }) icon: string;
    @Prop({ reflect: true }) size: string;
    @Prop({ reflect: true }) hierarchy: string;
    @Prop({ reflect: true }) fullWidth: boolean;

    /**
     * If true, the button contains only an icon.
     */
    @Prop() showIconOnly: boolean;

    /**
     * Indicates a loading state
     */
    @Prop() loading: boolean;

    @Watch('loading')
    onLoading(): void {
        this.slottedElement.tagName === 'BUTTON' &&
            ((this.slottedElement as HTMLButtonElement).disabled = this.loading);
    }

    @State() iconToUse: string;

    @Event() buttonIsSqueezed: EventEmitter;

    @Listen('resize', { target: 'window' })
    public handleResize(): void {
        this.checkForSqueezing();
    }

    /**
     * The slotted button or anchor.
     */
    private slottedElement: HTMLButtonElement | HTMLAnchorElement;

    /**
     * Mirrors the disabled state from the button to the host element.
     */
    private updateDisabledState = () => {
        if (this.slottedElement.tagName !== 'BUTTON') return;
        this.el.toggleAttribute('disabled', (this.slottedElement as HTMLButtonElement).disabled);
    };

    private disabledStateObserver = new MutationObserver(this.updateDisabledState);

    componentWillLoad(): void {
        this.initialize();
    }

    private initialize() {
        this.slottedElement = this.el.querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement;
        this.isIconOnly();

        this.slottedElement.addEventListener('click', e => {
            e.stopPropagation();
        });

        this.slottedElement.tagName === 'BUTTON' &&
            this.loading &&
            ((this.slottedElement as HTMLButtonElement).disabled = true);

        this.disabledStateObserver.disconnect();
        this.disabledStateObserver.observe(this.slottedElement, { attributeFilter: ['disabled'] });
        this.updateDisabledState();
    }

    private handleSlotChange = () => {
        this.initialize();
    };

    private clickSlotted = () => {
        this.slottedElement.click();
    };

    private checkForSqueezing = () => {
        if (this.slottedElement?.offsetWidth > this.el.offsetWidth) {
            this.buttonIsSqueezed.emit(true);
        }
    };

    private isIconOnly(): void {
        if (this.icon) {
            this.iconToUse = this.icon;
        } else {
            this.iconToUse = this.showIconOnly ? 'arrow-right' : 'arrow-east';
        }
    }

    componentDidRender(): void {
        this.checkForSqueezing();
    }

    render() {
        return (
            <Host
                onClick={this.clickSlotted}
                class={{
                    'ellipsis': this.ellipsis,
                    'icon-only': this.showIconOnly,
                    'loading': this.loading,
                }}
            >
                {this.icon && !this.loading && <sew-icon class="svg" icon={this.iconToUse}></sew-icon>}
                {!this.icon && !this.loading && <sew-icon class="svg" icon={this.iconToUse} />}
                {this.loading && <sew-icon class="svg loading" icon="circle-loader"></sew-icon>}
                <div class="text">
                    <slot onSlotchange={this.handleSlotChange}></slot>
                </div>
                {!!this.counter && <span class="counter">{this.counter}</span>}
            </Host>
        );
    }
}
